import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import TextInput from "../../components/forms/TextInput";
import { useAuth } from "../../js/hooks/useAuth";
import { getAgentProfile, saveAgentProfile } from "../../js/api/agentProfile";
import { getBrokerProfile, saveBrokerProfile } from "../../js/api/brokerProfile";
import {
  markProfileComplete,
  resolvePostAuthPath,
  isProfessionalRole,
  syncSessionOnboarding,
} from "../../js/auth/onboardingState";
import { setSession } from "../../js/auth/authService";
import { useStore } from "../../js/state/store";
import { updateRow } from "../../js/db";
import DeskPlatformBadge from "../../components/desk/DeskPlatformBadge";

export default function ProfessionalSetup() {
  const { session } = useAuth();
  const { dispatch } = useStore();
  const navigate = useNavigate();
  const isBroker = session?.role === "broker" || session?.role === "corporate";
  const profile = isBroker
    ? getBrokerProfile(session?.userId, session)
    : getAgentProfile(session?.userId, session);

  const [form, setForm] = useState({
    name: isBroker ? profile.broker_name : profile.agent_name,
    phone: isBroker ? profile.broker_phone : profile.agent_phone,
    license: isBroker ? profile.license : profile.dre_license,
    office: profile.office_name || session?.office_name || "",
    broker: isBroker ? "" : profile.broker_of_record || "",
  });
  const [error, setError] = useState("");

  if (!session || !isProfessionalRole(session.role)) {
    return (
      <AppLayout>
        <article className="card auth-card">
          <h1>Professional setup</h1>
          <p className="page-lead">Sign in as an agent or broker to continue.</p>
        </article>
      </AppLayout>
    );
  }

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!form.name.trim() || !form.license.trim() || !form.office.trim()) {
      setError("Name, license, and office are required.");
      return;
    }
    if (isBroker) {
      saveBrokerProfile(session.userId, {
        ...profile,
        broker_name: form.name.trim(),
        broker_phone: form.phone.trim(),
        license: form.license.trim(),
        office_name: form.office.trim(),
      });
    } else {
      saveAgentProfile(session.userId, {
        ...profile,
        agent_name: form.name.trim(),
        agent_phone: form.phone.trim(),
        dre_license: form.license.trim(),
        office_name: form.office.trim(),
        broker_of_record: form.broker.trim(),
      });
    }
    try {
      updateRow("users", session.userId, {
        name: form.name.trim(),
        office_name: form.office.trim(),
        license_number: form.license.trim(),
        phone: form.phone.trim(),
      });
    } catch {
      /* journey row may be absent in edge cases */
    }
    markProfileComplete(session.userId);
    const next = syncSessionOnboarding({
      ...session,
      name: form.name.trim(),
      office_name: form.office.trim(),
    });
    setSession(next);
    dispatch({ type: "SET_SESSION", payload: next });
    navigate(resolvePostAuthPath(next), { replace: true });
  }

  return (
    <AppLayout variant={isBroker ? "broker" : "agent"}>
      <article className="card auth-card onboarding-card">
        <DeskPlatformBadge />
        <h1>Complete your professional profile</h1>
        <p className="page-lead">
          License and office details appear on client-facing surfaces. You can refine them later in
          Account.
        </p>
        <form onSubmit={handleSubmit}>
          <TextInput label="Full name" name="name" value={form.name} onChange={update} />
          <TextInput label="Phone" name="phone" value={form.phone} onChange={update} />
          <TextInput label="License number" name="license" value={form.license} onChange={update} />
          <TextInput label="Office name" name="office" value={form.office} onChange={update} />
          {!isBroker ? (
            <TextInput
              label="Supervising broker"
              name="broker"
              value={form.broker}
              onChange={update}
              hint="Name or email of your broker of record."
            />
          ) : null}
          {error ? <p className="field-error">{error}</p> : null}
          <button type="submit" className="primary-btn btn-block">
            Save and continue
          </button>
        </form>
      </article>
    </AppLayout>
  );
}
